import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Check, Close, CloudUpload, Delete, WarningAmberRounded } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import { useAuth } from '../contexts/AuthContext';
import { isStudioScopedRole } from '../utils/roles';
import { useMyLocations, branchLabel, locationsWithRole } from '../hooks/useMyLocations';
import { buildClassPalette, isHexColor, resolveClassColors } from '../utils/classColors';

interface ClassType {
  _id: string;
  title: string;
  // 'workout' | 'wellness' | 'event' — derived server-side; picking a wellness
  // ClassType makes the resulting class/sessions category 'wellness' automatically.
  studioType?: 'workout' | 'wellness' | 'event';
}

interface ClassFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  classItem?: any;
  studioId?: string;
  enableSessionTypes?: boolean;
}

const ClassFormDialog: React.FC<ClassFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  classItem,
  studioId: studioIdProp = '',
  enableSessionTypes: enableSessionTypesProp = false,
}) => {
  const { admin } = useAuth();
  const isStudio = isStudioScopedRole(admin?.role);
  const isEditMode = Boolean(classItem);
  const isSuperAdmin = !isStudio;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');

  // Studio selector state for superadmin create mode
  const [studios, setStudios] = useState<any[]>([]);
  const [selectedStudioId, setSelectedStudioId] = useState('');

  // ── WHICH BRANCH DOES A NEW CLASS BELONG TO ───────────────────────────────
  //
  // A brand owner viewing "All locations" has no implied answer. Until now the
  // dialog fell back to `admin.studio._id` — the branch they happened to sign
  // in as — so the class appeared at a location nobody picked, and the only
  // clue was the branch chip on the row afterwards.
  //
  // Editing is never ambiguous: the class already lives somewhere, and
  // `effectiveStudioId` reads it off the row.
  const { locations, isAllLocations, roleByStudio } = useMyLocations();
  // Only where this account may create (manager or above); unfiltered when the
  // server did not send per-location roles.
  const creatableIds = locationsWithRole(roleByStudio, 'manager');
  const branchOptions = creatableIds
    ? locations.filter((l) => creatableIds.includes(String(l._id)))
    : locations;
  const [branchForNew, setBranchForNew] = useState('');
  const mustPickBranch = isAllLocations && !isEditMode;
  const [resolvedEnableSessionTypes, setResolvedEnableSessionTypes] = useState(enableSessionTypesProp);

  // Effective values derived from context
  const effectiveStudioId = isEditMode
    ? (classItem?.studio?._id || classItem?.studio || studioIdProp)
    : (isStudio
        ? (mustPickBranch ? branchForNew : (admin?.studio?._id || studioIdProp))
        : (selectedStudioId || studioIdProp));

  const enableSessionTypes = isEditMode ? enableSessionTypesProp : resolvedEnableSessionTypes;

  // Dropdown options
  const [instructors, setInstructors] = useState<any[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  // Rooms for the seat-layout picker. Empty unless the studio has seat
  // selection enabled, in which case the picker is hidden entirely.
  const [rooms, setRooms] = useState<any[]>([]);
  // The studio's classes — sizes the colour palette (one option per class)
  // and tells us which colours other classes already own.
  const [studioClasses, setStudioClasses] = useState<any[]>([]);
  const [studioClassesLoaded, setStudioClassesLoaded] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    className: '',
    image: '',
    description: '',
    duration: '',
    instructor: '',
    classType: '',
    price: '',
    payAtStudio: false,
    sessionType: 'group' as 'group' | 'private' | 'duet',
    // '' = no room; the class books by headcount exactly as before.
    seatLayout: '',
    // '' = no pick; the schedule calendar assigns one automatically.
    color: '',
  });

  // Fetch studios list for superadmin create mode
  useEffect(() => {
    if (open && isSuperAdmin && !isEditMode && !studioIdProp) {
      apiService.getStudios({}).then((res: any) => {
        setStudios(res.data || res.studios || []);
      }).catch(() => {});
    }
  }, [open, isSuperAdmin, isEditMode, studioIdProp]);

  // ── OUT-OF-ORDER RESPONSES ──────────────────────────────────────────────
  // Switching location fires a new load while the previous one may still be
  // in flight; a late answer for the OLD location must not overwrite the new
  // one's instructors / rooms / session-type flag. Each load takes a ticket
  // and only the latest ticket may write.
  const dropdownReqRef = useRef(0);
  const sessionTypeReqRef = useRef(0);
  const loadSessionTypeFlag = useCallback((sid: string) => {
    const req = ++sessionTypeReqRef.current;
    apiService.getStudioById(sid).then((res: any) => {
      if (req !== sessionTypeReqRef.current) return;
      const s = res.studio || res;
      setResolvedEnableSessionTypes(s?.studioPermissions?.enableSessionTypes || false);
    }).catch(() => {});
  }, []);

  // When a studio is selected by superadmin, fetch its permissions
  useEffect(() => {
    if (!selectedStudioId) return;
    loadSessionTypeFlag(selectedStudioId);
  }, [selectedStudioId, loadSessionTypeFlag]);

  const fetchDropdownData = useCallback(async () => {
    const currentStudioId = effectiveStudioId;
    const req = ++dropdownReqRef.current;
    const stale = () => req !== dropdownReqRef.current;
    if (!currentStudioId) {
      // No studio yet — just load class types for reference
      apiService.getClassTypes().then((res: any) => {
        if (!stale()) setClassTypes(res.classTypes || []);
      }).catch(() => {});
      setInstructors([]);
      setRooms([]);
      setStudioClasses([]);
      return;
    }
    // Only feeds the optional colour picker — a failure just hides it.
    setStudioClassesLoaded(false);
    apiService.getStudioClasses(currentStudioId)
      .then((res: any) => setStudioClasses(res.classes || []))
      .catch(() => setStudioClasses([]))
      .finally(() => setStudioClassesLoaded(true));
    try {
      const [instructorsRes, classTypesRes] = await Promise.all([
        apiService.getInstructorsByStudio(currentStudioId, {}),
        apiService.getClassTypes(),
      ]);
      if (stale()) return;
      setInstructors(instructorsRes.instructors || []);
      setClassTypes(classTypesRes.classTypes || []);

      // Rooms are optional and gated per studio: a 403 just means seat selection
      // is off here, so the picker stays hidden rather than showing an error.
      try {
        const roomsRes: any = await apiService.getSeatLayouts(currentStudioId);
        if (stale()) return;
        setRooms(roomsRes?.seatLayouts || roomsRes?.data?.seatLayouts || []);
      } catch {
        if (!stale()) setRooms([]);
      }
    } catch (err: any) {
      if (stale()) return;
      console.error('Failed to fetch dropdown data:', err);
      setError('Failed to load data');
    }
  }, [effectiveStudioId]);

  const populateForm = useCallback(() => {
    if (!classItem) return;
    
    const imageUrl = classItem.image || '';
    setFormData({
      className: classItem.className || '',
      image: imageUrl,
      description: classItem.description || '',
      duration: classItem.duration?.toString() || '',
      instructor: classItem.instructor?._id || classItem.instructor || '',
      classType: classItem.classType?._id || classItem.classType || '',
      price: classItem.price?.toString() || '',
      payAtStudio: classItem.payAtStudio || false,
      sessionType: (classItem.sessionType as 'group' | 'private' | 'duet') || 'group',
      seatLayout: classItem.seatLayout?._id || classItem.seatLayout || '',
      color: isHexColor(classItem.color) ? classItem.color.toUpperCase() : '',
    });
    setImageUrlInput(imageUrl);
  }, [classItem]);

  useEffect(() => {
    if (open) {
      fetchDropdownData();
      if (classItem) {
        populateForm();
      } else {
        resetForm();
        setSelectedStudioId('');
        setBranchForNew('');
        sessionTypeReqRef.current++; // drop any in-flight flag load
        setResolvedEnableSessionTypes(enableSessionTypesProp);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classItem]);

  // Re-fetch instructors when superadmin selects a studio
  useEffect(() => {
    if (open && selectedStudioId) {
      fetchDropdownData();
    }
  }, [selectedStudioId, open, fetchDropdownData]);

  // Same for the location a brand owner picks under "All locations":
  // instructors, rooms and the session-type flag are all per-location, so
  // they reload (pinned to it — fetchDropdownData follows effectiveStudioId)
  // and any instructor/room picked from the previous location is dropped.
  useEffect(() => {
    if (!open || !mustPickBranch || !branchForNew) return;
    setFormData(prev => ({ ...prev, instructor: '', seatLayout: '' }));
    fetchDropdownData();
    loadSessionTypeFlag(branchForNew);
  }, [branchForNew, open, mustPickBranch, fetchDropdownData, loadSessionTypeFlag]);

  const resetForm = () => {
    setFormData({
      className: '',
      image: '',
      description: '',
      duration: '',
      instructor: '',
      classType: '',
      price: '',
      payAtStudio: false,
      sessionType: 'group' as 'group' | 'private' | 'duet',
      seatLayout: '',
      color: '',
    });
    setImageUrlInput('');
  };

  // One suggested colour per class (counting the one being created), and the
  // colours other classes already use so no two classes share one.
  const otherClasses = studioClasses.filter((c: any) => c._id !== classItem?._id);
  const colorPalette = buildClassPalette(otherClasses.length + 1);
  const colorOwners = new Map<string, string>();
  otherClasses.forEach((c: any) => {
    if (isHexColor(c.color)) colorOwners.set(c.color.toUpperCase(), c.className);
  });
  const colorClash = formData.color ? colorOwners.get(formData.color.toUpperCase()) : undefined;

  // No "auto" state: once the studio's classes are known, an empty colour is
  // filled with what the calendar already shows for this class (edit) or the
  // first suggestion nobody uses (create), so the picker always has a value.
  useEffect(() => {
    if (!open || formData.color || !studioClassesLoaded) return;
    const resolved = classItem
      ? resolveClassColors(studioClasses).get(classItem._id)
      : colorPalette.find((hex) => !colorOwners.has(hex));
    if (resolved) setFormData((prev) => (prev.color ? prev : { ...prev, color: resolved.toUpperCase() }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studioClassesLoaded, studioClasses, classItem]);

  const [hexDraft, setHexDraft] = useState('');
  useEffect(() => {
    setHexDraft(formData.color);
  }, [formData.color]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(`File "${file.name}" is not an image file`);
      event.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(`Image "${file.name}" size should be less than 5MB`);
      event.target.value = '';
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Upload file to S3 (admin endpoint supports multiple files, but we'll send one)
      const response = await apiService.uploadFiles([file], 'classes');
      const imageUrls = response.fileLinks || [];

      if (imageUrls.length > 0) {
        // Set the first uploaded image URL
        setFormData(prev => ({
          ...prev,
          image: imageUrls[0],
        }));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: '',
    }));
  };

  // A class edit can reach this class's upcoming sessions or only sessions
  // created from now on. When a field sessions copy from the class changed,
  // ask the studio which before saving (the choice is saved in the audit log).
  const [scopePrompt, setScopePrompt] = useState<{
    upcomingSessions: number;
    bookedUpcomingSessions: number;
    ownInstructorSessions: number;
    ownPriceSessions: number;
  } | null>(null);
  const originalInstructorId = classItem?.instructor?._id || classItem?.instructor || '';
  const priceChanged =
    isEditMode && formData.price !== '' && Number(formData.price) !== Number(classItem?.price);
  const instructorChanged =
    isEditMode && Boolean(formData.instructor) && formData.instructor !== originalInstructorId;
  const instructorName = (id: string) =>
    instructors.find((i: any) => i._id === id)?.name ||
    (id === originalInstructorId ? classItem?.instructor?.name : '') ||
    'Unassigned';
  const idOf = (v: any) => (v?._id ?? v ?? '').toString();
  const payChanged = isEditMode && Boolean(formData.payAtStudio) !== Boolean(classItem?.payAtStudio);
  const roomChanged = isEditMode && rooms.length > 0 && (formData.seatLayout || '') !== idOf(classItem?.seatLayout);
  // Fields other than price/instructor that sessions copy from the class.
  const otherChanges = isEditMode
    ? [
        formData.className !== (classItem?.className || '') && 'name',
        formData.image !== (classItem?.image || '') && 'image',
        formData.description !== (classItem?.description || '') && 'description',
        formData.duration !== (classItem?.duration?.toString() || '') && 'duration',
        formData.classType !== idOf(classItem?.classType) && 'class type',
        enableSessionTypes && formData.sessionType !== (classItem?.sessionType || 'group') && 'session type',
        payChanged && 'payment method',
        roomChanged && 'room',
      ].filter(Boolean) as string[]
    : [];
  const sessionFieldsChanged = priceChanged || instructorChanged || otherChanges.length > 0;

  const handleUpdateClick = async () => {
    if (!sessionFieldsChanged) {
      handleSubmit(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const impact = await apiService.getClassUpdateImpact(classItem._id, effectiveStudioId);
      if (impact.upcomingSessions > 0) {
        setScopePrompt(impact);
        return;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Could not check upcoming sessions');
      return;
    } finally {
      setLoading(false);
    }
    handleSubmit(false);
  };

  const handleSubmit = async (applyToExistingSessions?: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        className: formData.className,
        image: formData.image,
        description: formData.description,
        duration: parseInt(formData.duration),
        instructor: formData.instructor,
        classType: formData.classType,
        price: parseFloat(formData.price),
        payAtStudio: formData.payAtStudio,
        ...(enableSessionTypes && { sessionType: formData.sessionType }),
        // Explicit null (not undefined) so choosing "No room" actually clears it.
        ...(rooms.length ? { seatLayout: formData.seatLayout || null } : {}),
        color: formData.color || null,
      };

      if (isEditMode) {
        // Pin the write to the class's OWN location, so editing a row from
        // another branch works while "All locations" is selected.
        await apiService.updateClass(
          classItem._id,
          { ...payload, applyToExistingSessions: applyToExistingSessions === true },
          effectiveStudioId,
        );
      } else {
        if (!effectiveStudioId) {
          setError(
            mustPickBranch
              ? 'Choose which location this class belongs to'
              : 'Please select a studio first',
          );
          return;
        }
        await apiService.createClass(effectiveStudioId, payload);
      }

      setScopePrompt(null);
      onSuccess();
      onClose();
    } catch (err: any) {
      setScopePrompt(null);
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditMode ? 'Edit Class' : 'Create Class'}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Location picker — a brand owner creating while viewing "All". */}
          {mustPickBranch && (
            <FormControl fullWidth size="small" required>
              <InputLabel>Location *</InputLabel>
              <Select
                value={branchForNew}
                label="Location *"
                onChange={(e) => setBranchForNew(e.target.value)}
              >
                {branchOptions.map((l) => (
                  <MenuItem key={l._id} value={l._id}>
                    {branchLabel(l)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Studio selector — superadmin create mode only */}
          {isSuperAdmin && !isEditMode && !studioIdProp && (
            <FormControl fullWidth size="small" required>
              <InputLabel>Studio *</InputLabel>
              <Select
                value={selectedStudioId}
                label="Studio *"
                onChange={(e) => setSelectedStudioId(e.target.value)}
              >
                {studios.map((s: any) => (
                  <MenuItem key={s._id} value={s._id}>{s.studioName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Class Name */}
          <TextField
            label="Class Name *"
            value={formData.className}
            onChange={(e) => handleChange('className', e.target.value)}
            fullWidth
            size="small"
          />

          {/* Image Upload */}
          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Image *
            </Typography>
            {formData.image ? (
              <Box position="relative" display="inline-block">
                <Box
                  component="img"
                  src={formData.image}
                  alt="Class"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={uploadingImage}
                fullWidth
                sx={{
                  py: 2,
                  borderStyle: 'dashed',
                }}
              >
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
            )}
            {/* Manual URL Input (optional) */}
            {!formData.image && (
              <Box mt={1}>
                <TextField
                  label="Or enter Image URL"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  onBlur={() => {
                    if (imageUrlInput.trim()) {
                      handleChange('image', imageUrlInput.trim());
                    }
                  }}
                  fullWidth
                  size="small"
                  helperText="You can upload an image or paste an image URL"
                />
              </Box>
            )}
          </Box>

          {/* Description */}
          <TextField
            label="Description *"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
          />

          {/* Duration and Price */}
          <Box display="flex" gap={2}>
            <TextField
              label="Duration (mins) *"
              type="number"
              value={formData.duration}
              onChange={(e) => handleChange('duration', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              size="small"
            />
            <TextField
              label="Price (₹) *"
              type="number"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              size="small"
            />
          </Box>

          {/* Instructor and Class Type */}
          <Box display="flex" gap={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Instructor *</InputLabel>
              <Select
                value={formData.instructor}
                onChange={(e) => handleChange('instructor', e.target.value)}
                label="Instructor *"
              >
                {/* A disabled instructor must never be newly assignable — but the
                    class's own currently-assigned instructor stays visible even if
                    they've since been disabled, so editing an existing class never
                    silently drops its coach from the list. */}
                {instructors
                  .filter((inst: any) => !inst.isDisabled || inst._id === formData.instructor)
                  .map((inst: any) => (
                    <MenuItem key={inst._id} value={inst._id}>
                      {inst.name}
                      {inst.isDisabled ? ' — disabled' : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Class Type *</InputLabel>
              <Select
                value={formData.classType}
                onChange={(e) => handleChange('classType', e.target.value)}
                label="Class Type *"
              >
                {classTypes.map((ct) => (
                  <MenuItem key={ct._id} value={ct._id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {ct.title}
                      {ct.studioType === 'wellness' && (
                        <Chip label="Wellness" size="small" sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600 }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Session Type — only when studio has enableSessionTypes permission */}
          {/* Room / seat layout — only for studios with seat selection on. */}
          {rooms.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Room</InputLabel>
              <Select
                value={rooms.some((r: any) => r._id === formData.seatLayout) ? formData.seatLayout : ''}
                onChange={(e) => handleChange('seatLayout', e.target.value)}
                label="Room"
              >
                <MenuItem value="">
                  <em>No room — book by headcount</em>
                </MenuItem>
                {rooms.map((room: any) => (
                  <MenuItem key={room._id} value={room._id}>
                    {room.name} · {room.bookableCount} bikes
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formData.seatLayout
                  ? "Members pick a bike. Each session's capacity is set from the room."
                  : 'Leave empty for ordinary headcount booking.'}
              </FormHelperText>
            </FormControl>
          )}

          {enableSessionTypes && (
            <FormControl fullWidth size="small">
              <InputLabel>Session Type</InputLabel>
              <Select
                value={formData.sessionType}
                label="Session Type"
                onChange={(e) => handleChange('sessionType', e.target.value)}
              >
                <MenuItem value="group">Group</MenuItem>
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="duet">Duet</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Calendar colour — tints this class's sessions in the schedule calendar. */}
          {effectiveStudioId && (
            <Box>
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                Calendar colour
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.25}>
                Tints this class's sessions in the schedule calendar. Each class needs its own colour.
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {/* Native colour picker behind a swatch */}
                <Box
                  component="label"
                  sx={{
                    position: 'relative',
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    backgroundColor: formData.color || '#FFFFFF',
                    border: '1px solid #E4DDDF',
                    boxShadow: 'inset 0 0 0 3px #FFFFFF',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:focus-within': { outline: '2px solid #333333', outlineOffset: 2 },
                  }}
                >
                  <input
                    type="color"
                    aria-label="Pick a calendar colour"
                    value={(formData.color || '#E8435A').toLowerCase()}
                    onChange={(e) => handleChange('color', e.target.value.toUpperCase())}
                    style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer', border: 0, padding: 0 }}
                  />
                </Box>
                <TextField
                  label="Hex"
                  size="small"
                  value={hexDraft}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const next = (raw.startsWith('#') ? raw : `#${raw}`).toUpperCase().slice(0, 7);
                    setHexDraft(next);
                    if (isHexColor(next)) handleChange('color', next);
                  }}
                  onBlur={() => setHexDraft(formData.color)}
                  error={Boolean(colorClash) || (hexDraft.length > 0 && !isHexColor(hexDraft))}
                  inputProps={{ maxLength: 7, spellCheck: false, style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
                  sx={{ width: 130 }}
                />
                <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
                  {colorPalette.map((hex) => {
                    const owner = colorOwners.get(hex);
                    const selected = formData.color === hex;
                    return (
                      <Tooltip key={hex} title={owner ? `Used by ${owner}` : hex}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={Boolean(owner)}
                            onClick={() => handleChange('color', hex)}
                            aria-label={owner ? `${hex}, used by ${owner}` : `Use colour ${hex}`}
                            sx={{
                              width: 26,
                              height: 26,
                              p: 0,
                              backgroundColor: hex,
                              opacity: owner ? 0.25 : 1,
                              border: selected ? '2px solid #333333' : '2px solid transparent',
                              boxShadow: selected ? '0 0 0 2px #ffffff inset' : 'none',
                              '&:hover': { backgroundColor: hex, transform: 'scale(1.08)' },
                              '&.Mui-disabled': { backgroundColor: hex },
                            }}
                          >
                            {selected && <Check sx={{ fontSize: 15, color: '#ffffff' }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
              {colorClash && (
                <FormHelperText error sx={{ mt: 0.75 }}>
                  {colorClash} already uses this colour. Pick a different one.
                </FormHelperText>
              )}
            </Box>
          )}

          {/* Pay at Studio Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.payAtStudio}
                onChange={(e) => handleChange('payAtStudio', e.target.checked)}
              />
            }
            label="Pay at Studio"
          />

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={isEditMode ? handleUpdateClick : () => handleSubmit()}
          variant="contained"
          disabled={loading || !formData.className || !formData.duration || !formData.price || !formData.instructor || !formData.classType || Boolean(colorClash)}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>

      <Dialog
        open={!!scopePrompt}
        onClose={loading ? undefined : () => setScopePrompt(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxWidth: 560 } }}
      >
        {scopePrompt && (() => {
          const n = scopePrompt.upcomingSessions;
          const sessions = (c: number) => `${c} session${c === 1 ? '' : 's'}`;
          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberRounded color="warning" />
                Update existing sessions too?
              </DialogTitle>
              <DialogContent>
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.75, py: 1.25, mb: 2 }}>
                  {priceChanged && (
                    <Typography variant="body2">
                      Price: ₹{classItem?.price} → <strong>₹{Number(formData.price)}</strong>
                    </Typography>
                  )}
                  {instructorChanged && (
                    <Typography variant="body2">
                      Instructor: {instructorName(originalInstructorId)} →{' '}
                      <strong>{instructorName(formData.instructor)}</strong>
                    </Typography>
                  )}
                  {otherChanges.length > 0 && (
                    <Typography variant="body2">
                      {priceChanged || instructorChanged ? 'Also changed' : 'Changed'}: {otherChanges.join(', ')}
                    </Typography>
                  )}
                </Box>
                <DialogContentText mb={2}>
                  <strong>{classItem?.className}</strong> has {sessions(n)} coming up
                  {scopePrompt.bookedUpcomingSessions > 0 && `, ${scopePrompt.bookedUpcomingSessions} with bookings`}.
                  Should they get these changes?
                </DialogContentText>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    disabled={loading}
                    onClick={() => handleSubmit(false)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', py: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>Only new sessions</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Sessions you create from now on use the new details. The {sessions(n)} already
                        scheduled stay exactly as they are.
                      </Typography>
                    </Box>
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={loading}
                    onClick={() => handleSubmit(true)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', py: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>Upcoming sessions too</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        The {sessions(n)} coming up get the new details as well. Past sessions never change.
                      </Typography>
                      {instructorChanged && scopePrompt.ownInstructorSessions > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          • {sessions(scopePrompt.ownInstructorSessions)} with a different instructor (a cover) keep that instructor.
                        </Typography>
                      )}
                      {priceChanged && scopePrompt.ownPriceSessions > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          • {sessions(scopePrompt.ownPriceSessions)} with their own price keep it.
                        </Typography>
                      )}
                      {priceChanged && scopePrompt.bookedUpcomingSessions > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          • Members who already booked keep what they paid. The new price applies to new bookings.
                        </Typography>
                      )}
                      {(payChanged || roomChanged) && scopePrompt.bookedUpcomingSessions > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          • {payChanged && roomChanged ? 'Payment method and room' : payChanged ? 'Payment method' : 'Room'} won't
                          change on the {sessions(scopePrompt.bookedUpcomingSessions)} that already have bookings.
                        </Typography>
                      )}
                    </Box>
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                  You can still change single sessions through Edit or Bulk Edit.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setScopePrompt(null)} disabled={loading}>
                  Cancel
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Dialog>
  );
};

export default ClassFormDialog;

